<template>
  <div>
    <AppLayout title="Clientes">
      <template #actions>
        <button type="button" class="btn btn-primary" @click="openNew"><i class="bi bi-plus-lg me-1"></i> Novo Cliente</button>
      </template>

      <div class="mb-3">
        <input
          type="text"
          class="form-control"
          style="max-width: 320px"
          placeholder="Buscar por nome, usuário, e-mail ou loja..."
          v-model="search"
          @input="onSearch"
        >
      </div>

      <div class="table-card">
        <table class="table">
          <thead>
            <tr>
              <th>Loja / Dono</th>
              <th>Usuário</th>
              <th>Plano</th>
              <th>Assinatura</th>
              <th>Status</th>
              <th>Última venda</th>
              <th></th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="client in clientsList" :key="client.id">
              <td>
                <router-link :to="{ name: 'client-detail', params: { id: client.id } }">
                  {{ client.name_business || client.name }}
                </router-link>
                <div class="text-muted small" v-if="client.name_business">{{ client.name }}</div>
              </td>
              <td>{{ client.username }}</td>
              <td>{{ client.plan?.name ?? '—' }}</td>
              <td>
                <span v-if="client.plan_expires_at" class="badge" :class="subscriptionBadgeClass(client.plan_expires_at)">
                  {{ formatter.date(client.plan_expires_at) }}
                </span>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <span class="badge" :class="client.active ? 'text-bg-success' : 'text-bg-secondary'">
                  {{ client.active ? 'Ativo' : 'Inativo' }}
                </span>
              </td>
              <td>
                <span v-if="client.last_sale_at" class="text-muted">{{ formatter.date(client.last_sale_at) }}</span>
                <span v-else class="badge text-bg-secondary">Nunca vendeu</span>
              </td>
              <td style="width:44px"><button @click="openEdit(client)" type="button" class="btn btn-outline-secondary btn-sm" title="Editar"><i class="bi bi-pencil-square"></i></button></td>
              <td style="width:44px"><button @click="openPlan(client)" type="button" class="btn btn-outline-secondary btn-sm" title="Trocar plano"><i class="bi bi-card-checklist"></i></button></td>
              <td style="width:44px"><button @click="openResetPassword(client)" type="button" class="btn btn-outline-secondary btn-sm" title="Trocar senha"><i class="bi bi-key-fill"></i></button></td>
            </tr>
            <tr v-if="!clientsList.length">
              <td colspan="9" class="text-center text-muted">Nenhum cliente encontrado.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav aria-label="Paginação de clientes" class="mt-3" v-if="totalPages > 1">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
              <i class="bi bi-chevron-left"></i> Anterior
            </button>
          </li>
          <li class="page-item" v-for="p in totalPages" :key="p" :class="{ active: p === currentPage }">
            <button class="page-link" @click="changePage(p)">{{ p }}</button>
          </li>
          <li class="page-item" :class="{ disabled: currentPage === totalPages }">
            <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
              Próximo <i class="bi bi-chevron-right"></i>
            </button>
          </li>
        </ul>
      </nav>
    </AppLayout>

    <!-- Modal Cliente (novo/editar) -->
    <div class="modal fade" id="modal_client" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editMode ? 'Editar Cliente' : 'Novo Cliente' }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="submitClient">
            <div class="modal-body">
              <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>

              <div class="row">
                <div class="mb-3 col">
                  <label class="form-label">Nome do dono:</label>
                  <input v-model="form.name" required type="text" class="form-control">
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Loja:</label>
                  <input v-model="form.name_business" type="text" class="form-control">
                </div>
              </div>

              <div class="row">
                <div class="mb-3 col">
                  <label class="form-label">Usuário (login):</label>
                  <input v-model="form.username" :readonly="editMode" required type="text" class="form-control">
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Telefone:</label>
                  <input v-model="form.phone" type="text" class="form-control">
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">E-mail:</label>
                <input v-model="form.email" required type="email" class="form-control">
              </div>

              <div class="row" v-if="!editMode">
                <div class="mb-3 col">
                  <label class="form-label">Senha:</label>
                  <input v-model="form.password" required type="password" class="form-control" minlength="8">
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Confirmar senha:</label>
                  <input v-model="form.password_confirmation" required type="password" class="form-control" minlength="8">
                </div>
              </div>

              <div class="row" v-if="!editMode">
                <div class="mb-3 col">
                  <label class="form-label">Plano:</label>
                  <select v-model="form.plan_id" class="form-select">
                    <option :value="null">Sem plano</option>
                    <option v-for="plan in plansList" :key="plan.id" :value="plan.id">{{ plan.name }}</option>
                  </select>
                </div>
                <div class="mb-3 col">
                  <label class="form-label">Assinatura válida até:</label>
                  <input v-model="form.plan_expires_at" type="date" class="form-control">
                  <div class="form-text">Só serve de alerta — não bloqueia o acesso do cliente.</div>
                </div>
              </div>

              <div class="form-check" v-if="editMode">
                <input v-model="form.active" class="form-check-input" type="checkbox" id="client_active">
                <label class="form-check-label" for="client_active">Cliente ativo (pode acessar o sistema)</label>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Trocar Senha -->
    <div class="modal fade" id="modal_password" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Trocar senha de {{ selectedClient?.name }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="submitResetPassword">
            <div class="modal-body">
              <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>
              <p class="text-muted small">Isso desconecta o cliente de todos os dispositivos onde ele estiver logado.</p>
              <div class="mb-3">
                <label class="form-label">Nova senha:</label>
                <input v-model="passwordForm.password" required type="password" class="form-control" minlength="8">
              </div>
              <div class="mb-3">
                <label class="form-label">Confirmar nova senha:</label>
                <input v-model="passwordForm.password_confirmation" required type="password" class="form-control" minlength="8">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Trocar senha' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Trocar Plano -->
    <div class="modal fade" id="modal_plan" tabindex="-1" aria-hidden="true" data-bs-backdrop="static">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Plano de {{ selectedClient?.name }}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <form @submit.prevent="submitPlan">
            <div class="modal-body">
              <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>
              <div class="mb-3">
                <label class="form-label">Plano:</label>
                <select v-model="planForm.plan_id" class="form-select">
                  <option :value="null">Sem plano</option>
                  <option v-for="plan in plansList" :key="plan.id" :value="plan.id">{{ plan.name }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Assinatura válida até:</label>
                <input v-model="planForm.plan_expires_at" type="date" class="form-control">
                <div class="form-text">Só serve de alerta pro admin — não bloqueia o acesso do cliente nem os cadastros dele.</div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">{{ saving ? 'Salvando...' : 'Salvar' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Modal Feedback -->
    <div class="modal fade" id="modal_feedback" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Sucesso</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>{{ feedbackMessage }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive, ref } from 'vue';
import AppLayout from "@/components/AppLayout.vue";
import { Modal } from "bootstrap";
import { clients } from "@/models/clients";
import { plans } from "@/models/plans";
import formatter from "@/services/formatter";

const emptyForm = () => ({
  id: null,
  username: '',
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  name_business: '',
  phone: '',
  plan_id: null,
  plan_expires_at: '',
  active: true,
});

// `plan_expires_at` só alerta o admin (ver EnforcesPlanLimits no backend) —
// nunca bloqueia o cliente. Cores: vermelho = vencida, amarelo = vence em
// até 7 dias, verde = em dia.
function subscriptionBadgeClass(planExpiresAt) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiresAt = new Date(planExpiresAt + 'T00:00:00');
  const diffDays = Math.round((expiresAt - today) / 86400000);

  if (diffDays < 0) return 'text-bg-danger';
  if (diffDays <= 7) return 'text-bg-warning';
  return 'text-bg-success';
}

export default {
  name: 'ClientsView',
  components: { AppLayout },
  setup() {
    const Clients = clients();
    const Plans = plans();

    const clientsList = ref([]);
    const plansList = ref([]);
    const search = ref('');
    const currentPage = ref(1);
    const totalPages = ref(1);
    const editMode = ref(false);
    const saving = ref(false);
    const formError = ref('');
    const feedbackMessage = ref('');
    const selectedClient = ref(null);

    const form = reactive(emptyForm());
    const passwordForm = reactive({ password: '', password_confirmation: '' });
    const planForm = reactive({ plan_id: null, plan_expires_at: '' });

    let searchTimeout = null;
    let modals = {};

    function openModal(id) {
      modals[id] = modals[id] ?? new Modal(document.getElementById(id));
      modals[id].show();
    }

    function closeModal(id) {
      modals[id]?.hide();
    }

    async function fetchClients() {
      const response = await Clients.list({ search: search.value || undefined, page: currentPage.value, per_page: 20 });
      clientsList.value = response?.data ?? [];
      totalPages.value = response?.pages ?? 1;
    }

    async function fetchPlans() {
      plansList.value = await Plans.list();
    }

    function onSearch() {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        fetchClients();
      }, 350);
    }

    function changePage(page) {
      if (page < 1 || page > totalPages.value) return;
      currentPage.value = page;
      fetchClients();
    }

    function openNew() {
      Object.assign(form, emptyForm());
      formError.value = '';
      editMode.value = false;
      openModal('modal_client');
    }

    function openEdit(client) {
      Object.assign(form, {
        id: client.id,
        username: client.username,
        name: client.name,
        email: client.email,
        password: '',
        password_confirmation: '',
        name_business: client.name_business ?? '',
        phone: client.phone ?? '',
        plan_id: client.plan_id ?? null,
        active: client.active,
      });
      formError.value = '';
      editMode.value = true;
      openModal('modal_client');
    }

    async function submitClient() {
      saving.value = true;
      formError.value = '';
      try {
        if (editMode.value) {
          await Clients.update(form.id, {
            name: form.name,
            email: form.email,
            name_business: form.name_business || null,
            phone: form.phone || null,
            active: form.active,
          });
          feedbackMessage.value = 'Cliente atualizado com sucesso!';
        } else {
          await Clients.create({
            username: form.username,
            name: form.name,
            email: form.email,
            password: form.password,
            password_confirmation: form.password_confirmation,
            name_business: form.name_business || null,
            phone: form.phone || null,
            plan_id: form.plan_id,
            plan_expires_at: form.plan_expires_at || null,
          });
          feedbackMessage.value = 'Cliente cadastrado com sucesso!';
        }
        closeModal('modal_client');
        openModal('modal_feedback');
        await fetchClients();
      } catch (err) {
        formError.value = firstErrorMessage(err);
      } finally {
        saving.value = false;
      }
    }

    function openResetPassword(client) {
      selectedClient.value = client;
      passwordForm.password = '';
      passwordForm.password_confirmation = '';
      formError.value = '';
      openModal('modal_password');
    }

    async function submitResetPassword() {
      saving.value = true;
      formError.value = '';
      try {
        await Clients.resetPassword(selectedClient.value.id, { ...passwordForm });
        feedbackMessage.value = 'Senha atualizada com sucesso!';
        closeModal('modal_password');
        openModal('modal_feedback');
      } catch (err) {
        formError.value = firstErrorMessage(err);
      } finally {
        saving.value = false;
      }
    }

    function openPlan(client) {
      selectedClient.value = client;
      planForm.plan_id = client.plan_id ?? null;
      planForm.plan_expires_at = client.plan_expires_at ?? '';
      formError.value = '';
      openModal('modal_plan');
    }

    async function submitPlan() {
      saving.value = true;
      formError.value = '';
      try {
        await Clients.updatePlan(selectedClient.value.id, planForm.plan_id, planForm.plan_expires_at || null);
        feedbackMessage.value = 'Plano atualizado com sucesso!';
        closeModal('modal_plan');
        openModal('modal_feedback');
        await fetchClients();
      } catch (err) {
        formError.value = firstErrorMessage(err);
      } finally {
        saving.value = false;
      }
    }

    function firstErrorMessage(err) {
      const firstError = Object.values(err?.errors ?? {})[0];
      return firstError?.[0] ?? err?.message ?? 'Não foi possível concluir a ação.';
    }

    onMounted(() => {
      fetchClients();
      fetchPlans();
    });

    return {
      clientsList, plansList, search, currentPage, totalPages, editMode, saving, formError,
      feedbackMessage, selectedClient, form, passwordForm, planForm, formatter,
      onSearch, changePage, openNew, openEdit, submitClient,
      openResetPassword, submitResetPassword, openPlan, submitPlan, subscriptionBadgeClass,
    };
  },
}
</script>
